import React, {useCallback, useContext, useMemo} from "react";
import {Button, Typography} from "@mui/material";
import DashboardTwoToneIcon from "@mui/icons-material/DashboardTwoTone";
import clsx from "clsx";
import {useHistory} from "react-router-dom";
import {AppContext} from "../../../../App";
import {setOpenSnackbar} from "../../../../services/AppServices";
import {HomeStyle} from "../../../../styles/pages/home/HomeStyle";
import {getAccountInfo} from "../../../../utils/GenericUtils";
import {actions} from "../../../../utils/dictionaries/Actions";
import AppPage from "../../../widgets/layout/AppPage";
import ActionTile from "./ActionTile";
import {actionTileColors} from "./actionTileColors";

const hasActionAccess = (accountRoles = [], actionRoles = []) => (
	accountRoles.some((role) => actionRoles.some((actionRole) => actionRole === role))
);

const AllActions = () => {
	const classes = HomeStyle();
	const history = useHistory();
	const appContext = useContext(AppContext);
	const account = useMemo(() => getAccountInfo() || {}, []);
	const accountRoles = useMemo(() => account.roles || [], [account.roles]);

	const actionEntries = useMemo(() => Object.entries(actions), []);
	const enabledCount = useMemo(
		() => actionEntries.filter(([, action]) => hasActionAccess(accountRoles, action.roles)).length,
		[actionEntries, accountRoles]
	);

	const actionSelected = useCallback((action, enabled) => {
		if (enabled) {
			history.push(action.path);
			return;
		}

		setOpenSnackbar(
			"warning",
			"Non hai i permessi per aprire questa sezione. Chiedi l'abilitazione al tuo referente.",
			appContext.dispatch
		);
	}, [history, appContext.dispatch]);

	return (
		<AppPage
			title="Azioni"
			appBarProps={{back: true, backPosition: "/gestione"}}
			mainClassName={classes.page}
			containerClassName={classes.container}
		>
			<section className={classes.actionsPageHero}>
				<div>
					<Typography className={classes.eyebrow}>Atlantis workspace</Typography>
					<Typography component="h1" className={classes.actionsPageTitle}>
						Tutte le azioni
					</Typography>
					<Typography className={classes.actionsPageSubtitle}>
						Le sezioni operative disponibili nell'app, con evidenza rapida di quelle abilitate per il tuo profilo.
					</Typography>
				</div>
				<div className={classes.actionsPageStats}>
					<div className={classes.actionsPageStat}>
						<Typography className={classes.metricLabel}>Abilitate</Typography>
						<Typography className={classes.metricValue}>{enabledCount}</Typography>
					</div>
					<div className={classes.actionsPageStat}>
						<Typography className={classes.metricLabel}>Totali</Typography>
						<Typography className={classes.metricValue}>{actionEntries.length}</Typography>
					</div>
				</div>
			</section>

			<section className={clsx(classes.card, classes.allActionsCard)}>
				<header className={classes.cardHeader}>
					<div>
						<Typography className={classes.cardTitle}>Azioni disponibili</Typography>
						<Typography className={classes.cardSubtitle}>
							Apri una sezione o individua subito le azioni per cui serve una nuova abilitazione.
						</Typography>
					</div>
					<Button
						aria-label="Torna alla dashboard"
						className={classes.actionsDashboardButton}
						startIcon={<DashboardTwoToneIcon/>}
						variant="outlined"
						size="small"
						onClick={() => history.push("/gestione")}
					>
						Dashboard
					</Button>
				</header>

				<div className={classes.allActionsGrid}>
					{actionEntries.map(([key, action], index) => {
						const enabled = hasActionAccess(accountRoles, action.roles);

						return (
							<ActionTile
								key={key}
								action={action}
								classes={classes}
								colors={actionTileColors[index % actionTileColors.length]}
								descriptionLimit={96}
								disabled={!enabled}
								onSelect={() => actionSelected(action, enabled)}
							/>
						);
					})}
				</div>
			</section>
		</AppPage>
	);
};

export default React.memo(AllActions);
